import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield,
  Settings,
  Bell,
  Lock,
  Globe,
  Users,
  Database,
  Download,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Copy,
  RefreshCw,
  Monitor,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { data: organization } = useUserOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("general");
  const [isRegeneratingToken, setIsRegeneratingToken] = useState(false);
  const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);

  const dohUrl = organization?.dns_token
    ? `https://dns.fendly.org/dns-query/${organization.dns_token}`
    : null;

  async function handleRegenerateToken() {
    if (!organization?.id) return;
    setIsRegeneratingToken(true);
    try {
      const { error } = await supabase.rpc("regenerate_dns_token", {
        org_id: organization.id,
      });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["organization", user?.id] });
      toast.success("DNS token regenerated. Update your device with the new URL.");
    } catch (err: unknown) {
      toast.error("Failed to regenerate token: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsRegeneratingToken(false);
    }
  }

  async function generateMobileConfig() {
    if (!dohUrl) return;
    setIsGeneratingProfile(true);
    try {
      // Resolve ALL A records for dns.fendly.org.
      // Fallback = the two known Cloudflare edge IPs (from dig dns.fendly.org).
      // Never fall back to 1.1.1.1 — that IP has no cert for dns.fendly.org.
      let serverIps: string[] = ['104.21.59.225', '172.67.184.207'];
      try {
        const res = await fetch(
          'https://cloudflare-dns.com/dns-query?name=dns.fendly.org&type=A',
          { headers: { Accept: 'application/dns-json' } }
        );
        const data = await res.json();
        const ips = data.Answer
          ?.filter((r: { type: number; data: string }) => r.type === 1)
          .map((r: { type: number; data: string }) => r.data);
        if (ips?.length > 0) serverIps = ips;
      } catch {
        // use hardcoded fallback
      }

      const profileUuid = crypto.randomUUID();
      const payloadUuid = crypto.randomUUID();
      const serverAddressesXml = serverIps
        .map(ip => `\t\t\t\t\t<string>${ip}</string>`)
        .join('\n');

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
\t<key>PayloadContent</key>
\t<array>
\t\t<dict>
\t\t\t<key>DNSSettings</key>
\t\t\t<dict>
\t\t\t\t<key>DNSProtocol</key>
\t\t\t\t<string>HTTPS</string>
\t\t\t\t<key>ServerAddresses</key>
\t\t\t\t<array>
${serverAddressesXml}
\t\t\t\t</array>
\t\t\t\t<key>ServerURL</key>
\t\t\t\t<string>${dohUrl}</string>
\t\t\t</dict>
\t\t\t<key>PayloadDescription</key>
\t\t\t<string>Configures your device to use Fendly DNS filtering over HTTPS</string>
\t\t\t<key>PayloadDisplayName</key>
\t\t\t<string>Fendly DNS</string>
\t\t\t<key>PayloadIdentifier</key>
\t\t\t<string>org.fendly.dns.${payloadUuid}</string>
\t\t\t<key>PayloadType</key>
\t\t\t<string>com.apple.dnsSettings.managed</string>
\t\t\t<key>PayloadUUID</key>
\t\t\t<string>${payloadUuid}</string>
\t\t\t<key>PayloadVersion</key>
\t\t\t<integer>1</integer>
\t\t\t<key>ProhibitDisablement</key>
\t\t\t<false/>
\t\t</dict>
\t</array>
\t<key>PayloadDescription</key>
\t<string>Routes all DNS queries through your personal Fendly filtered server</string>
\t<key>PayloadDisplayName</key>
\t<string>Fendly DNS Filtering</string>
\t<key>PayloadIdentifier</key>
\t<string>org.fendly.dns.profile.${profileUuid}</string>
\t<key>PayloadOrganization</key>
\t<string>Fendly</string>
\t<key>PayloadRemovalDisallowed</key>
\t<false/>
\t<key>PayloadScope</key>
\t<string>System</string>
\t<key>PayloadType</key>
\t<string>Configuration</string>
\t<key>PayloadUUID</key>
\t<string>${profileUuid}</string>
\t<key>PayloadVersion</key>
\t<integer>1</integer>
</dict>
</plist>`;

      const blob = new Blob([xml], { type: 'application/x-apple-aspen-config' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fendly-dns.mobileconfig';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Profile downloaded — open the file to install it.');
    } catch {
      toast.error('Failed to generate profile');
    } finally {
      setIsGeneratingProfile(false);
    }
  }

  // Settings state
  const [settings, setSettings] = useState({
    // General Settings
    organizationName: organization?.name || "My Organization",
    timezone: "UTC",
    language: "en",

    // Security Settings
    enableTwoFactor: false,
    requirePasswordChange: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,

    // DNS Settings
    enableDNSSEC: true,
    enableDNSSECValidation: true,
    enableDNSCaching: true,
    dnsCacheTTL: 300,

    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    securityAlerts: true,
    weeklyReports: true,

    // Privacy Settings
    dataRetention: 90,
    anonymizeData: false,
    shareAnalytics: true,

    // Advanced Settings
    enableDebugMode: false,
    enableBetaFeatures: false,
    logLevel: "info",
  });

  const handleToggleChange = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    toast.success("Setting updated", { id: 'setting-changed' });
  };

  const handleTextChange = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = () => {
    // TODO: Implement actual settings save to backend
    toast.success("Settings saved successfully");
  };

  const handleResetSettings = () => {
    // TODO: Implement settings reset
    toast.info("Settings reset to defaults");
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "fendly-settings.json";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Settings exported successfully");
  };

  if (!organization) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">
                  Please set up your organization first
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "3rem",
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">
                  Configure your DNS security and organization preferences
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleExportSettings}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" onClick={handleResetSettings}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button onClick={handleSaveSettings}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>

            {/* Settings Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="dns">DNS</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
              </TabsList>

              {/* General Settings */}
              <TabsContent value="general" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Organization Settings
                    </CardTitle>
                    <CardDescription>
                      Basic organization information and preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="orgName">Organization Name</Label>
                        <Input
                          id="orgName"
                          value={settings.organizationName}
                          onChange={(e) =>
                            handleTextChange("organizationName", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="timezone">Timezone</Label>
                        <Select
                          value={settings.timezone}
                          onValueChange={(value) =>
                            handleToggleChange("timezone", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UTC">UTC</SelectItem>
                            <SelectItem value="America/New_York">
                              Eastern Time
                            </SelectItem>
                            <SelectItem value="America/Chicago">
                              Central Time
                            </SelectItem>
                            <SelectItem value="America/Denver">
                              Mountain Time
                            </SelectItem>
                            <SelectItem value="America/Los_Angeles">
                              Pacific Time
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="language">Language</Label>
                      <Select
                        value={settings.language}
                        onValueChange={(value) =>
                          handleToggleChange("language", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Security Settings */}
              <TabsContent value="security" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5" />
                      Authentication & Security
                    </CardTitle>
                    <CardDescription>
                      Configure security settings and access controls
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="2fa">Two-Factor Authentication</Label>
                        <p className="text-sm text-muted-foreground">
                          Require 2FA for all users
                        </p>
                      </div>
                      <Switch
                        id="2fa"
                        checked={settings.enableTwoFactor}
                        onCheckedChange={(checked) =>
                          handleToggleChange("enableTwoFactor", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="passwordChange">
                          Require Password Change
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Force password changes every 90 days
                        </p>
                      </div>
                      <Switch
                        id="passwordChange"
                        checked={settings.requirePasswordChange}
                        onCheckedChange={(checked) =>
                          handleToggleChange("requirePasswordChange", checked)
                        }
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="sessionTimeout">
                          Session Timeout (minutes)
                        </Label>
                        <Input
                          id="sessionTimeout"
                          type="number"
                          value={settings.sessionTimeout}
                          onChange={(e) =>
                            handleTextChange("sessionTimeout", parseInt(e.target.value))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="maxAttempts">Max Login Attempts</Label>
                        <Input
                          id="maxAttempts"
                          type="number"
                          value={settings.maxLoginAttempts}
                          onChange={(e) =>
                            handleTextChange("maxLoginAttempts", parseInt(e.target.value))
                          }
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* DNS Settings */}
              <TabsContent value="dns" className="space-y-6">

                {/* DoH Setup Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      DNS-over-HTTPS Setup
                    </CardTitle>
                    <CardDescription>
                      Configure your device or router to use Fendly DNS filtering over HTTPS (RFC 8484).
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {dohUrl ? (
                      <>
                        <div>
                          <Label>Your DNS-over-HTTPS URL</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono break-all">
                              {dohUrl}
                            </code>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(dohUrl);
                                toast.success("Copied to clipboard");
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Keep this URL private — it identifies your organization.
                          </p>
                        </div>

                        <Separator />

                        <div className="space-y-3">
                          <p className="text-sm font-medium">Configure your device</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                            <div className="rounded-lg border p-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <Monitor className="size-4" /> macOS
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Download the profile, double-click it, then go to System Settings → Privacy &amp; Security → Profiles and click Install.
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={generateMobileConfig}
                                disabled={isGeneratingProfile}
                                className="w-full"
                              >
                                <Download className="size-3.5 mr-1.5" />
                                {isGeneratingProfile ? 'Generating…' : 'Download profile (.mobileconfig)'}
                              </Button>
                            </div>

                            <div className="rounded-lg border p-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <Smartphone className="size-4" /> iPhone / iPad
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Open this page in <strong>Safari</strong> on your device and tap Download. Then go to Settings → General → VPN &amp; Device Management → tap the profile → Install.
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={generateMobileConfig}
                                disabled={isGeneratingProfile}
                                className="w-full"
                              >
                                <Download className="size-3.5 mr-1.5" />
                                {isGeneratingProfile ? 'Generating…' : 'Download profile (.mobileconfig)'}
                              </Button>
                            </div>

                            <div className="rounded-lg border p-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                Windows 11
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Settings → Network &amp; Internet → your Wi-Fi → DNS server assignment → Edit → Manual → IPv4 on → set DNS over HTTPS to <em>On (manual template)</em> → paste your Fendly URL.
                              </p>
                            </div>

                            <div className="rounded-lg border p-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                Firefox / Chrome
                              </div>
                              <p className="text-xs text-muted-foreground">
                                <strong>Firefox:</strong> Settings → Privacy &amp; Security → DNS over HTTPS → Custom → paste URL.<br />
                                <strong>Chrome:</strong> Settings → Security → Use secure DNS → With: Custom → paste URL.
                              </p>
                            </div>

                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <p className="text-sm font-medium">Token rotation</p>
                          <p className="text-xs text-muted-foreground">
                            If your URL was compromised, regenerate your token. The old URL stops working immediately.
                          </p>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleRegenerateToken}
                            disabled={isRegeneratingToken}
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isRegeneratingToken ? "animate-spin" : ""}`} />
                            {isRegeneratingToken ? "Regenerating…" : "Regenerate token"}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No organization found. Create an organization to get your DNS-over-HTTPS URL.
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      DNS Configuration
                    </CardTitle>
                    <CardDescription>
                      Configure DNS security and performance settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="dnssec">Enable DNSSEC</Label>
                        <p className="text-sm text-muted-foreground">
                          Use DNSSEC for enhanced security
                        </p>
                      </div>
                      <Switch
                        id="dnssec"
                        checked={settings.enableDNSSEC}
                        onCheckedChange={(checked) =>
                          handleToggleChange("enableDNSSEC", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="dnssecValidation">
                          DNSSEC Validation
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Validate DNSSEC signatures
                        </p>
                      </div>
                      <Switch
                        id="dnssecValidation"
                        checked={settings.enableDNSSECValidation}
                        onCheckedChange={(checked) =>
                          handleToggleChange("enableDNSSECValidation", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="dnsCache">DNS Caching</Label>
                        <p className="text-sm text-muted-foreground">
                          Enable DNS response caching
                        </p>
                      </div>
                      <Switch
                        id="dnsCache"
                        checked={settings.enableDNSCaching}
                        onCheckedChange={(checked) =>
                          handleToggleChange("enableDNSCaching", checked)
                        }
                      />
                    </div>

                    <div>
                      <Label htmlFor="cacheTTL">Cache TTL (seconds)</Label>
                      <Input
                        id="cacheTTL"
                        type="number"
                        value={settings.dnsCacheTTL}
                        onChange={(e) =>
                          handleTextChange("dnsCacheTTL", parseInt(e.target.value))
                        }
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Notification Settings */}
              <TabsContent value="notifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Notification Preferences
                    </CardTitle>
                    <CardDescription>
                      Configure how you receive alerts and updates
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="emailNotif">Email Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive notifications via email
                        </p>
                      </div>
                      <Switch
                        id="emailNotif"
                        checked={settings.emailNotifications}
                        onCheckedChange={(checked) =>
                          handleToggleChange("emailNotifications", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="pushNotif">Push Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive push notifications in browser
                        </p>
                      </div>
                      <Switch
                        id="pushNotif"
                        checked={settings.pushNotifications}
                        onCheckedChange={(checked) =>
                          handleToggleChange("pushNotifications", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="securityAlerts">Security Alerts</Label>
                        <p className="text-sm text-muted-foreground">
                          Immediate alerts for security threats
                        </p>
                      </div>
                      <Switch
                        id="securityAlerts"
                        checked={settings.securityAlerts}
                        onCheckedChange={(checked) =>
                          handleToggleChange("securityAlerts", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="weeklyReports">Weekly Reports</Label>
                        <p className="text-sm text-muted-foreground">
                          Summary reports every week
                        </p>
                      </div>
                      <Switch
                        id="weeklyReports"
                        checked={settings.weeklyReports}
                        onCheckedChange={(checked) =>
                          handleToggleChange("weeklyReports", checked)
                        }
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
